import React from 'react';
import IdentityLink from './IdentityLink';
import {
  MapPin,
  Navigation,
  Package,
  Truck,
  ShieldCheck,
  UserRound,
  TriangleAlert,
} from 'lucide-react';
import Button from 'v3/components/Button';
import { validatePermission } from 'actions';
import { formatMoney, formatDate, formatDecimal, formatDocument } from './contract';
const names = (items) =>
  (items || [])
    .map((item) => item.name)
    .filter(Boolean)
    .join(' / ');
const place = (point) =>
  [point?.city, point?.province].filter(Boolean).join(' - ') || 'Não informado';
function Detail({ icon: Icon, label, children }) {
  return (
    <div className="tc-k-detail">
      <Icon size={14} aria-hidden="true" />
      <span>
        <span className="tc-k-label">{label}: </span>
        {children || 'Não informado'}
      </span>
    </div>
  );
}
export default function KanbanCard({ item, onVehicles, onOther, vehiclesAvailable, vehicleCount }) {
  const kind = { PRE_LOAD: 'Pré Carga', LOAD: 'Carga', TRAVEL: 'Viagem' }[item.type];
  // Sem veículo apto: Veículos em cinza e Atender com outro direto no card.
  const noVehicles = vehicleCount?.state === 'CALCULATED' && vehicleCount.eligibleCount === 0;
  const canAttendOther =
    noVehicles &&
    vehiclesAvailable &&
    !!onOther &&
    validatePermission(item.type === 'PRE_LOAD' ? 'ATENDER_PRECARGA' : 'ATENDER_CARGA');
  const identityPath =
    item.type === 'PRE_LOAD'
      ? `/pre-cargas/${item.loadId}`
      : item.type === 'LOAD'
        ? `/cargas/${item.loadId}`
        : `/viagens/${item.travelId}`;
  const profile = item.vehicleProfile || {};
  const vehicle = [
    names(profile.vehicleTypes),
    names(profile.bodyTypes),
    names(profile.implementTypes),
  ]
    .filter(Boolean)
    .join(' · ');
  const tracking =
    item.tracking?.required === true
      ? 'Sim'
      : item.tracking?.required === false
        ? 'Não'
        : 'Não informado';
  const quoted = item.amount === null && item.sourceAmount !== null;
  const amount = formatMoney(
    quoted ? item.sourceAmount : item.amount,
    quoted ? item.sourceCurrency || null : 'BRL',
  );
  const observations = [item.observations?.comments, item.observations?.additionalInfo].filter(
    Boolean,
  );
  const plates = [item.vehicle?.plate, ...(item.wagons || []).map((wagon) => wagon.plate)]
    .filter(Boolean)
    .join(' / ');
  const routes = [
    {
      name: 'Coleta agendada',
      point: item.origin,
      date: item.scheduledPickupAt,
      Icon: MapPin,
      points: item.origins || [],
    },
    {
      name: 'Entrega agendada',
      point: item.destination,
      date: item.scheduledDeliveryAt,
      Icon: Navigation,
      points: item.destinations || [],
    },
  ];
  return (
    <article className="tc-k-card" data-item-key={item.key} aria-label={`${kind} ${item.id}`}>
      <header className="tc-k-card-header">
        <div className="tc-k-client">
          <h3 title={item.client?.name || ''}>{item.client?.name || 'Cliente não informado'}</h3>
          <div className="tc-k-document">
            {item.client?.cnpj?.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ'}:{' '}
            {formatDocument(item.client?.cnpj)}
          </div>
        </div>
        <div className="tc-k-identity">
          <span>
            {kind}{' '}
            <IdentityLink to={identityPath} aria-label={`Abrir ${kind.toLowerCase()} ${item.id}`}>
              #{item.id}
            </IdentityLink>
          </span>
          {item.externalLoadId ? (
            <span title={item.externalLoadId}>Ref. {item.externalLoadId}</span>
          ) : (
            item.travelNumber && <span title={item.travelNumber}>{item.travelNumber}</span>
          )}
          <strong className="tc-k-amount">{amount}</strong>
        </div>
      </header>
      {quoted && <p className="tc-k-note">Valor original; total em reais não informado.</p>}
      <div className="tc-k-route">
        {routes.map(({ name, point, date, Icon, points }) => (
          <React.Fragment key={name}>
            <div className="tc-k-route-row">
              <Icon size={14} aria-hidden="true" />
              <span title={place(point)}>{place(point)}</span>
              <time
                dateTime={date || undefined}
                title={name}
                aria-label={`${name}: ${formatDate(date)}`}
              >
                {formatDate(date)}
              </time>
            </div>
            {points.length > 1 && (
              <details className="tc-k-stops">
                <summary>
                  {points.length} locais de {name.startsWith('Coleta') ? 'coleta' : 'entrega'}
                </summary>
                <ul>
                  {points.map((stop) => (
                    <li key={stop.id}>
                      {place(stop)} · {formatDate(stop.scheduledAt)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </React.Fragment>
        ))}
      </div>
      <Detail icon={Package} label="Produto">
        {item.product?.name || 'Não informado'}
        {item.quantity?.weight != null && ` · ${formatDecimal(item.quantity.weight)} kg`}
        {item.quantity?.volume != null && ` · Volume: ${item.quantity.volume}`}
        {item.quantity?.pallets != null && ` · Pallets: ${item.quantity.pallets}`}
      </Detail>
      <Detail icon={Truck} label="Veículo">
        {vehicle}
      </Detail>
      <Detail icon={ShieldCheck} label="Rastreado">
        {tracking}
        {item.tracking?.tracker?.name && ` · ${item.tracking.tracker.name}`}
      </Detail>
      <Detail icon={ShieldCheck} label="Gerenciadora">
        {names(item.tracking?.riskManagers)}
      </Detail>
      {item.moppRequired === true && (
        <Detail icon={ShieldCheck} label="MOPP">
          Exigido pela carga
        </Detail>
      )}
      {item.type === 'TRAVEL' && (
        <>
          <Detail icon={UserRound} label="Motorista">
            {item.driver?.name}
          </Detail>
          <Detail icon={Truck} label="Placas">
            {plates}
          </Detail>
        </>
      )}
      {observations.length > 0 && (
        <div className="tc-k-observations">
          <TriangleAlert size={14} aria-hidden="true" />
          <div>
            {observations.map((note, i) => (
              <p key={`${i}-${note}`}>{note}</p>
            ))}
          </div>
        </div>
      )}
      {canAttendOther && (
        <div className="tc-k-other">
          <Button size="xs" variant="danger" outlined onClick={() => onOther(item)}>
            Atender com outro
          </Button>
        </div>
      )}
      {item.type !== 'TRAVEL' && (
        <Button
          className="tc-k-vehicles"
          size="sm"
          variant={noVehicles ? 'secondary' : undefined}
          disabled={!vehiclesAvailable}
          title={
            !vehiclesAvailable
              ? 'Indisponível no momento'
              : vehicleCount?.state === 'CALCULATED'
                ? `${vehicleCount.eligibleCount} elegíveis · ${vehicleCount.incompleteCount} com cadastro incompleto`
                : vehicleCount?.message || `Veículos para ${kind} ${item.id}`
          }
          onClick={() => onVehicles(item)}
        >
          <Truck size={15} aria-hidden="true" /> Veículos
          {vehicleCount?.state === 'CALCULATED' && ` (${vehicleCount.eligibleCount})`}
        </Button>
      )}
    </article>
  );
}
